import React from "react";
import * as yup from "yup";

import {useFeedbackTools} from "../../components/FeedbackTools";
import useDialog from "../../hooks/useDialog";
import TextAttributeForm from "../../components/AttributeForm/TextAttributeForm";
import MenuDots from "../../components/MenuDots";
import EmailEditor from "./EmailEditor";
import useEmailEditor from "./useEmailEditor";
import {useTemplates} from "./TemplatesContext";
import LanguageSelect from "./LanguageSelect";
import ThemeDialog from "./ThemeDialog";

import {useTheme} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TopBarSelect from "./TopBarSelect";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaletteIcon from '@mui/icons-material/Palette';
import EditIcon from '@mui/icons-material/Edit';
import Typography from "@mui/material/Typography";


const nameValidation = yup.object().shape({
    name: yup
        .string()
        .typeError('Deve essere in formato testuale')
        .required('Campo obbligatorio')
})

const TemplateEditor = (props) => {
    const {type, templateVariables, alertElement = null} = props;

    const {
        templates,
        selectedTemplate,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        handleTemplateSelection,
        isUpdatingSelectedTemplate,
        isCreatingNewModel
    } = useTemplates();

    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();
    const theme = useTheme();
    const formTemplate = useDialog();
    const themeDialog = useDialog();
    const editDialog = useDialog();

    const emailEditor = useEmailEditor(type, templateVariables, "IT");

    const handleSave = async () => {
        try {
            await updateTemplate(selectedTemplate.id, {
                userVariables: emailEditor.templateUserVariables,
                locale: emailEditor.locale
            });
            emailEditor.resetTemplateUserVariable();
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const handleUseDefault = async () => {
        try {
            await updateTemplate(selectedTemplate.id, {isDefault: true});
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const handleCreate = async (data) => {
        const template = await createTemplate({...data, locale: emailEditor.locale, type: type});
        handleTemplateSelection(template.id);
        formTemplate.close();
    }

    const handleEdit = async (data) => {
        try {
            await updateTemplate(selectedTemplate.id, data);
            showSuccessNotification("Modifica effettuata");
            editDialog.close();
        } catch (e) {
            showErrorNotification(e);
        }
    }

    return <>

        <Stack spacing={3} sx={{mb: 3}}>
            <Card sx={{mt: 3}}>
                <Paper elevation={0}
                       sx={{
                           p: '2px 16px',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: "space-between",
                           minHeight: "48px",
                           color: theme.palette.text.primary
                       }}>
                    <Stack direction="row" spacing={2} alignItems='center'>
                        <TopBarSelect options={templates}
                                      selected={selectedTemplate.id ? selectedTemplate : null}
                                      label="Seleziona modello"
                                      onSelection={(t) => handleTemplateSelection(t.id)}
                                      renderOption={(t) => <Stack direction="row" spacing={1} alignItems='center'>
                                          <Typography variant="button">{t.name}</Typography> {t.isDefault &&
                                          <CheckCircleOutlineIcon fontSize="small"/>}
                                      </Stack>}
                                      createLabel="Aggiungi modello"
                                      createOption
                                      onCreate={formTemplate.open}/>
                        <LanguageSelect onSelection={emailEditor.handleLocaleChange}/>
                        <MenuDots options={[
                            {
                                label: "Logo e tema",
                                icon: <PaletteIcon/>,
                                disabled: emailEditor.selectedTemplateId === null,
                                onClick: themeDialog.open,
                            },
                            {
                                label: "Modifica nome",
                                icon: <EditIcon/>,
                                disabled: emailEditor.selectedTemplateId === null,
                                onClick: editDialog.open,
                            },
                            {
                                label: "Usa come default",
                                icon: <CheckCircleOutlineIcon/>,
                                disabled: emailEditor.selectedTemplateId === null,
                                onClick: handleUseDefault,
                            },
                            {
                                label: "Elimina",
                                icon: <DeleteIcon/>,
                                disabled: emailEditor.selectedTemplateId === null,
                                onClick: () => deleteTemplate(selectedTemplate.id),
                            },
                        ]}
                                  renderOption={(option) => option.element}
                                  onOptionClick={(option) => option.onClick()}/>
                        <Divider sx={{height: 28, m: 0.5}} orientation="vertical"/>
                    </Stack>
                    <Button onClick={handleSave} disabled={!emailEditor.isEdited}>Salva</Button>
                </Paper>
            </Card>
            {
                alertElement !== null && alertElement
            }
            <EmailEditor emailEditor={emailEditor} boxProps={{mx: 2}}/>
        </Stack>

        {
            formTemplate.show && <TextAttributeForm dialogTitle="Crea nuovo modello"
                                                    attribute="name"
                                                    label="Nome modello"
                                                    validationSchema={nameValidation}
                                                    onClose={formTemplate.close}
                                                    isLoading={isCreatingNewModel}
                                                    onSubmit={handleCreate}/>
        }

        {
            editDialog.show && <TextAttributeForm dialogTitle="Modifica modello"
                                                  attribute="name"
                                                  label="Nome modello"
                                                  validationSchema={nameValidation}
                                                  onClose={editDialog.close}
                                                  isLoading={isUpdatingSelectedTemplate}
                                                  onSubmit={handleEdit}/>
        }

        {
            themeDialog.show && <ThemeDialog onClose={themeDialog.close}
                                             onSave={emailEditor.loadRendering}/>
        }
    </>
}

export default TemplateEditor;